import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { glass } from "./portal";
import { useProject } from "@/lib/portal/useProject";
import { motion, AnimatePresence } from "framer-motion";
import { Send, Paperclip, Smile } from "lucide-react";
import { format } from "date-fns";

export const Route = createFileRoute("/_authenticated/portal/messages")({
  component: MessagesPage,
});

function MessagesPage() {
  const qc = useQueryClient();
  const { data: project } = useProject();
  const [text, setText] = useState("");
  const [typing, setTyping] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);

  const { data: messages } = useQuery({
    queryKey: ["portal", "chat"],
    queryFn: async () => (await supabase.from("project_messages").select("*").order("created_at")).data ?? [],
    enabled: !!project,
    refetchInterval: 5000,
  });

  useEffect(() => { listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!text.trim() || !project) return;
    const { data: user } = await supabase.auth.getUser();
    await supabase.from("project_messages").insert({
      project_id: project.id, sender_id: user.user!.id, sender_role: "client", body: text,
    });
    setText("");
    qc.invalidateQueries({ queryKey: ["portal", "chat"] });
    // Simulate team typing + auto-reply
    setTyping(true);
    setTimeout(async () => {
      await supabase.from("project_messages").insert({
        project_id: project.id, sender_id: user.user!.id, sender_role: "team",
        body: "Ciao 👋 Il team FlashWeb ti risponderà entro poche ore. Grazie!",
      });
      setTyping(false);
      qc.invalidateQueries({ queryKey: ["portal", "chat"] });
    }, 2500);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-4">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">Messaggi</h1>
        <p className="text-white/60 mt-1">Chat diretta con il team FlashWeb.</p>
      </div>
      <div className={`${glass} flex flex-col h-[70vh]`}>
        <div className="border-b border-white/5 px-5 py-3 flex items-center gap-3">
          <div className="h-9 w-9 rounded-full bg-gradient-to-br from-fuchsia-500 to-cyan-400 grid place-items-center text-xs font-bold text-black">FW</div>
          <div>
            <div className="text-sm font-medium">Team FlashWeb</div>
            <div className="text-xs text-emerald-300 flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" /> Online</div>
          </div>
        </div>
        <div ref={listRef} className="flex-1 overflow-y-auto p-5 space-y-3">
          {(messages ?? []).map((m) => {
            const client = m.sender_role === "client";
            return (
              <motion.div key={m.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                className={`flex ${client ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm ${
                  client ? "bg-gradient-to-br from-fuchsia-500 to-cyan-400 text-black" : "bg-white/5 border border-white/10"}`}>
                  <div>{m.body}</div>
                  <div className={`text-[10px] mt-1 opacity-60 ${client ? "text-black/70" : "text-white/50"}`}>
                    {format(new Date(m.created_at), "HH:mm")}
                  </div>
                </div>
              </motion.div>
            );
          })}
          <AnimatePresence>
            {typing && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex">
                <div className="bg-white/5 border border-white/10 rounded-2xl px-3 py-2 flex gap-1">
                  {[0, 0.15, 0.3].map((d) => (
                    <motion.span key={d} animate={{ y: [0, -3, 0] }} transition={{ repeat: Infinity, duration: 0.8, delay: d }}
                      className="h-1.5 w-1.5 rounded-full bg-white/60 block" />
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-white/5 p-3 flex items-center gap-2">
          <button type="button" className="p-2 text-white/40 hover:text-white"><Paperclip className="h-4 w-4" /></button>
          <button type="button" onClick={() => setText((t) => t + "👍")} className="p-2 text-white/40 hover:text-white"><Smile className="h-4 w-4" /></button>
          <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Scrivi un messaggio..." className="flex-1 bg-transparent focus:outline-none text-sm" />
          <button type="submit" className="p-2 rounded-full bg-gradient-to-br from-fuchsia-500 to-cyan-400 text-black"><Send className="h-4 w-4" /></button>
        </form>
      </div>
    </div>
  );
}
